import React from "react"
import { FlexGrid } from "@grapecity/wijmo.grid"
import * as wjcGridPdf from "@grapecity/wijmo.grid.pdf"
import * as wjcGridXlsx from "@grapecity/wijmo.grid.xlsx"
import { Button, Col, Row } from "react-bootstrap"
import * as wjInput from "@grapecity/wijmo.react.input"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faFileExcel, faFilePdf } from "@fortawesome/free-regular-svg-icons"

/**
 *
 */
type TFooter = {
  pageSize: number
  setPageSize: Function
  gridData: any
  flexGrid: any
  pdfFileName?: string
  pdfFormatItem?: (args: wjcGridPdf.PdfFormatItemEventArgs) => void
  excelFileName?: string
  excelFormatItem?: (args: wjcGridXlsx.XlsxFormatItemEventArgs) => void
  isEnableExport?: boolean
  isEnablePageSize?: "visible" | "hidden" | undefined
  changePageSizeTargetNumberArray?: number[]
  excelIncludeColumns?: string[]
}

/**
 * 明細グリッドのフッター
 * @param pageSize ページサイズ
 * @param setPageSize ページサイズのセット変数
 * @param gridData グリッドデータ
 * @param flexGrid グリッド
 * @param isEnablePageSize ページング表示非表示
 * @param isEnableExport 出力ボタン表示非表示
 * @param pdfFileName PDFファイル名
 * @param excelFileName EXCELファイル名
 * @returns Reactコンポーネント。フッター（ページング機能、ファイル出力ボタン）
 */
const GridFooter: React.FC<TFooter> = ({
  pageSize,
  setPageSize,
  gridData,
  flexGrid,
  pdfFormatItem,
  excelFormatItem,
  isEnablePageSize = undefined,
  isEnableExport = true,
  pdfFileName = "fileName.pdf",
  excelFileName = "fileName.xlsx",
  children,
  excelIncludeColumns,
  changePageSizeTargetNumberArray: pageSizeArray = [10, 15, 20, 50, 100],
}) => {
  //#region ファイル出力処理
  const _exportPDF = (grid: FlexGrid) => {
    if (grid == null) return

    // おまじないbefore
    const pageSize = grid.itemsSource.pageSize
    const pageIndex = grid.itemsSource.pageIndex
    const selection = grid.selection
    const filter = grid.itemsSource.filter
    grid.itemsSource.filter = null
    grid.itemsSource.pageSize = 0

    // やりたいこと
    wjcGridPdf.FlexGridPdfConverter.export(grid, pdfFileName, {
      formatItem: pdfFormatItem,
      embeddedFonts: [
        {
          source: `${process.env.PUBLIC_URL}/font/ipaexg.ttf`,
          name: "ipaexg",
        },
      ],
      documentOptions: {
        header: {
          declarative: {
            text: "\t&[Page] / &[Pages]",
          },
        },
        footer: {
          declarative: {
            text: "\t&[Page] / &[Pages]",
          },
        },
      },
      styles: {
        cellStyle: {
          font: { family: "ipaexg" },
          backgroundColor: "#ffffff",
          borderColor: "#c6c6c6",
        },
        altCellStyle: {
          backgroundColor: "#f9f9f9",
        },
        groupCellStyle: {
          backgroundColor: "#dddddd",
        },
        headerCellStyle: {
          backgroundColor: "#eaeaea",
        },
      },
    })

    // おまじないafter
    grid.itemsSource.filter = filter
    grid.itemsSource.pageSize = pageSize
    grid.itemsSource.moveToPage(pageIndex)
    grid.selection = selection
  }

  const _exportExcel = (grid: FlexGrid) => {
    if (grid == null) return

    // おまじないbefore
    const pageSize = grid.itemsSource.pageSize
    const pageIndex = grid.itemsSource.pageIndex
    const selection = grid.selection
    const filter = grid.itemsSource.filter
    grid.itemsSource.filter = null
    grid.itemsSource.pageSize = 0

    // やりたいこと
    wjcGridXlsx.FlexGridXlsxConverter.saveAsync(
      grid,
      {
        includeColumnHeaders: true,
        includeStyles: true,
        includeColumns: excelIncludeColumns
          ? (column) =>
              excelIncludeColumns.find((c) => c === column.binding) != null
          : undefined,
        formatItem: excelFormatItem,
      },
      excelFileName
    )

    // おまじないafter
    grid.itemsSource.filter = filter
    grid.itemsSource.pageSize = pageSize
    grid.itemsSource.moveToPage(pageIndex)
    grid.selection = selection
  }
  //#endregion

  // ページング切り替え
  const changePageSize = (number: number) => {
    if (!flexGrid || flexGrid === undefined) return
    setPageSize(number)
    flexGrid.itemsSource.pageSize = number
    flexGrid.refresh()
  }

  return (
    <>
      <Row className="grid-footer-row">
        <Col className="grid-footer">
          <div
            style={{
              display: isEnablePageSize === "hidden" ? "none" : "block",
            }}>
            <table>
              <tbody>
                <tr>
                  <td>
                    <wjInput.CollectionViewNavigator
                      headerFormat="{currentPage:n0} / {pageCount:n0} ページ"
                      byPage={true}
                      cv={gridData.data}
                    />
                  </td>
                  <td>
                    <div style={{ marginLeft: "10px" }}>
                      <span>ページ表示件数: </span>
                      {pageSizeArray.map((targetPageSize, index) => (
                        <span
                          key={index}
                          onClick={() => changePageSize(targetPageSize)}
                          className={
                            pageSize !== targetPageSize
                              ? "page-size-clickable"
                              : ""
                          }>
                          {0 < index && " "}
                          {targetPageSize !== 0 ? targetPageSize : "すべて"}
                          {index < pageSizeArray.length - 1 && ","}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {children}
        </Col>
        {isEnableExport && (
          <Col>
            <table className="exportFooter">
              <tbody>
                <tr>
                  <td>
                    <div>
                      <span>ほかの形式にエクスポート</span>
                    </div>
                  </td>
                  <td>
                    <Button
                      className="pdf-export-button"
                      onClick={() => {
                        if (flexGrid == null) return
                        _exportPDF(flexGrid)
                      }}>
                      <FontAwesomeIcon icon={faFilePdf} />
                      <span>&nbsp;&nbsp;PDF</span>
                    </Button>
                  </td>
                  <td>
                    <Button
                      className="excel-export-button"
                      onClick={() => {
                        if (flexGrid == null) return
                        _exportExcel(flexGrid)
                      }}>
                      <FontAwesomeIcon icon={faFileExcel} />
                      <span>&nbsp;&nbsp;Excel</span>
                    </Button>
                  </td>
                </tr>
              </tbody>
            </table>
          </Col>
        )}
      </Row>
    </>
  )
}
export default GridFooter
